// Cache-bust Build v2.0.1
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { OfflineProvider } from '@/contexts/OfflineContext';
import { GlobalSearchProvider } from '@/contexts/GlobalSearchContext';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import OfflineIndicator from '@/components/OfflineIndicator';
import OfflineErrorBoundary from '@/components/OfflineErrorBoundary';
import { shouldAllowScreenshots, applyScreenshotProtection } from '@/utils/screenshotProtection';
import analyticsService from '@/utils/analyticsService';
import '@/styles/animations.css';

// Pages
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AuthCallback from '@/pages/AuthCallback';
import Landing from '@/pages/Landing';
import HowItWorks from '@/pages/HowItWorks';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import OnboardingFlow from '@/pages/OnboardingFlow';
import Dashboard from '@/pages/Dashboard';
import SmartDailyCheckIn from '@/pages/SmartDailyCheckIn';
import MultiStepCheckIn from '@/pages/MultiStepCheckIn';
import QuickHealthCheck from '@/pages/QuickHealthCheck';
import HealthInsight from '@/pages/HealthInsight';
import DailyHealthLoop from '@/pages/DailyHealthLoop';
import ProgressTracker from '@/pages/ProgressTracker';
import SimpleHealthDashboard from '@/pages/SimpleHealthDashboard';
import SymptomChecker from '@/pages/SymptomChecker';
import AIPersonalityGuide from '@/pages/AIPersonalityGuide';
import HealthStory from '@/pages/HealthStory';
import CheckinEvolution from '@/pages/CheckinEvolution';
import SmartNudgesDemo from '@/pages/SmartNudgesDemo';
import CulturalSettings from '@/pages/CulturalSettings';
import Habits from '@/pages/Habits';
import Medications from '@/pages/Medications';
import MedicationSupport from '@/pages/MedicationSupport';
import Insights from '@/pages/Insights';
import HealthInsightsDashboard from '@/pages/HealthInsightsDashboard';
import Nutrition from '@/pages/Nutrition';
import Notifications from '@/pages/Notifications';
import SupportHelp from '@/pages/SupportHelp';
import Profile from '@/pages/Profile';
import Trends from '@/pages/Trends';
import TalkToSpecialist from '@/pages/TalkToSpecialist';
import ExpertSupport from '@/pages/ExpertSupport';
import HealthHub from '@/pages/HealthHub';
import PrivacyNotice from '@/pages/PrivacyNotice';
import VendorRegister from '@/pages/VendorRegister';
import VendorLogin from '@/pages/VendorLogin';
import VendorDashboard from '@/pages/VendorDashboard';
import Orders from '@/pages/Orders';
import Subscriptions from '@/pages/Subscriptions';
import SubscriptionPlans from '@/pages/SubscriptionPlans';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import SubscriptionCancel from '@/pages/SubscriptionCancel';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminInsights from '@/pages/AdminInsights';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminLogin from '@/pages/AdminLogin';
import AdminUsers from '@/pages/AdminUsers';
import AdminEmailTrace from '@/pages/AdminEmailTrace';
import AdminUserCheckinSanity from '@/pages/AdminUserCheckinSanity';
import AdminSupportTickets from '@/pages/AdminSupportTickets';
import AdminHealthAlerts from '@/pages/AdminHealthAlerts';
import AdminFeedbackAnalytics from '@/pages/AdminFeedbackAnalytics';
import { BetaFeedbackProvider } from '@/contexts/BetaFeedbackContext';
import BetaFeedbackModal from '@/components/BetaFeedbackModal';
import FloatingFeedbackButton from '@/components/FloatingFeedbackButton';
import SmartFeedbackPrompt from '@/components/SmartFeedbackPrompt';
import CareServices from '@/pages/CareServices';
import ClinicianShareView from '@/pages/ClinicianShareView';
import EmailAnalytics from '@/pages/EmailAnalytics';
import StepTracker from '@/pages/StepTracker';
import StepTrackerEnhanced from '@/pages/StepTrackerEnhanced';
import CommunityHub from '@/pages/CommunityHub';
import PreventiveHealthTracker from '@/pages/PreventiveHealthTracker';
import HealthRiskCheck from '@/pages/HealthRiskCheck';
import HormonalHealth from '@/pages/HormonalHealth';
import MentalHealth from '@/pages/MentalHealth';
import EducationHub from '@/pages/EducationHub';
import WellnessCheckIn from '@/pages/WellnessCheckIn';
import WomensHealth from '@/pages/WomensHealth';
import MensHealth from '@/pages/MensHealth';
import ChildrensHealth from '@/pages/ChildrensHealth';
import ChildrensHub from '@/pages/children/ChildrensHub';
import ChildDailyCheckin from '@/pages/children/DailyCheckin';
import ChildQuickCheck from '@/pages/children/QuickCheck';
import ChildImmunization from '@/pages/children/Immunization';
import ChildGrowth from '@/pages/children/Growth';
import ChildNutrition from '@/pages/children/Nutrition';
import ChildBehaviour from '@/pages/children/Behaviour';
import MaternalHealth from '@/pages/MaternalHealth';
import MaternalHub from '@/pages/maternal/MaternalHub';
import MaternalDailyCheckin from '@/pages/maternal/DailyCheckin';
import MaternalSymptoms from '@/pages/maternal/Symptoms';
import MaternalWeek from '@/pages/maternal/Week';
import MaternalNutrition from '@/pages/maternal/Nutrition';
import MaternalAppointments from '@/pages/maternal/Appointments';
import MaternalPostpartum from '@/pages/maternal/Postpartum';
import MaternalTrends from '@/pages/maternal/Trends';
import FamilyHealth from '@/pages/FamilyHealth';
import UnifiedFamilyHealth from '@/pages/UnifiedFamilyHealth';
import RemindersSettings from '@/pages/RemindersSettings';
import MyReminders from '@/pages/MyReminders';
import BottomNav from '@/components/BottomNav';
import DesktopHeader from '@/components/DesktopHeader';

import { LanguageProvider } from '@/contexts/LanguageContext';
import '@/App.css';

// Screenshot Protection Wrapper
function ScreenshotProtectionWrapper({ children }) {
  const { user } = useAuth();

  useEffect(() => {
    // Check if screenshots should be allowed
    const allowScreenshots = shouldAllowScreenshots(user);
    
    // Apply or remove screenshot protection
    applyScreenshotProtection(allowScreenshots);
    
    // Log status for debugging
    if (allowScreenshots) {
      console.log('📸 Screenshot Mode: ENABLED');
      console.log('Environment:', process.env.REACT_APP_ENVIRONMENT);
      console.log('User:', user?.email || 'Not logged in');
    } else {
      console.log('🔒 Screenshot Mode: BLOCKED (Production)');
    }
  }, [user]);

  return children;
}

// Analytics Page Tracker & UI Cleanup
function AnalyticsPageTracker() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Initialize analytics when user logs in
    if (user) {
      analyticsService.init();
    }
  }, [user]);

  useEffect(() => {
    // Track page views
    if (user) {
      analyticsService.trackPageView(location.pathname);
    }
    
    // Clean up UI on route change to prevent stacking/overlapping
    // Reset scroll position
    window.scrollTo(0, 0);
    
    // Clean up any stale modal backdrops or overlays
    const cleanupOverlays = () => {
      const backdrops = document.querySelectorAll('[data-radix-portal]');
      backdrops.forEach(backdrop => {
        if (!backdrop.querySelector('[role="dialog"][data-state="open"]')) {
          backdrop.remove();
        }
      });
      
      // Reset body overflow if no modals are open
      const openModals = document.querySelectorAll('[role="dialog"][data-state="open"]');
      if (openModals.length === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
    
    // Run cleanup after navigation
    const timeoutId = setTimeout(cleanupOverlays, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, user]);

  return null;
}

function App() {
  return (
    <OfflineErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <OfflineProvider>
            <BrowserRouter>
              <GlobalSearchProvider>
                <BetaFeedbackProvider>
                  <ScreenshotProtectionWrapper>
                    <OfflineIndicator />
                    <AnalyticsPageTracker />
                    <AppShell />
                    {/* Global beta-feedback surfaces — modal is shared
                        by every CTA; floating button + smart prompt are
                        route-aware and self-hide on admin/auth pages. */}
                    <BetaFeedbackModal />
                    <FloatingFeedbackButton />
                    <SmartFeedbackPrompt />
                  </ScreenshotProtectionWrapper>
                </BetaFeedbackProvider>
              </GlobalSearchProvider>
            </BrowserRouter>
          </OfflineProvider>
        </AuthProvider>
      </LanguageProvider>
    </OfflineErrorBoundary>
  );
}

/**
 * AppShell — actual route tree. Lives INSIDE <BrowserRouter> so it can
 * subscribe to URL changes via `useLocation()`. Critically, the OAuth-
 * callback conditional below depends on the URL hash (`#session_id=…`);
 * keeping that check inside a `useLocation`-aware component is what lets
 * the AuthCallback overlay unmount the instant <AuthCallback> calls
 * `navigate('/dashboard')` — otherwise the parent App component never
 * re-renders (it has no react-router hooks) and the overlay sticks.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
function AppShell() {
  const location = useLocation();
  // /dashboard#session_id=... ; we intercept it here and render the
  // AuthCallback page which exchanges the session_id for a HealthLoop JWT.
  // Reactive because `location` is subscribed to history changes — when
  // AuthCallback's navigate() clears the hash, this re-evaluates to false
  // and the route tree renders Dashboard naturally.
  const oauthCallback = (location.hash || '').includes('session_id=');
  return (
    <div className="App">
      <DesktopHeader />
      {oauthCallback ? <AuthCallback /> : (
      <Routes>
            {/* Landing Page */}
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/p/clinician/:token" element={<ClinicianShareView />} />
            
            {/* Vendor Routes */}
            <Route path="/vendor-register" element={<VendorRegister />} />
            <Route path="/vendor-login" element={<VendorLogin />} />
            <Route path="/vendor-dashboard" element={<VendorDashboard />} />

            {/* Onboarding Route */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingFlow />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/smart-check-in"
              element={
                <ProtectedRoute>
                  <SmartDailyCheckIn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/check-in"
              element={
                <ProtectedRoute>
                  <MultiStepCheckIn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-check"
              element={
                <ProtectedRoute>
                  <QuickHealthCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-insight"
              element={
                <ProtectedRoute>
                  <HealthInsight />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-loop"
              element={
                <ProtectedRoute>
                  <DailyHealthLoop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <ProgressTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-dashboard"
              element={
                <ProtectedRoute>
                  <SimpleHealthDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/symptom-checker"
              element={
                <ProtectedRoute>
                  <SymptomChecker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-personality"
              element={
                <ProtectedRoute>
                  <AIPersonalityGuide />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-story"
              element={
                <ProtectedRoute>
                  <HealthStory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/evolution"
              element={
                <ProtectedRoute>
                  <CheckinEvolution />
                </ProtectedRoute>
              }
            />
            <Route
              path="/smart-nudges"
              element={
                <ProtectedRoute>
                  <SmartNudgesDemo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cultural-settings"
              element={
                <ProtectedRoute>
                  <CulturalSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/habits"
              element={
                <ProtectedRoute>
                  <Habits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/medications"
              element={
                <ProtectedRoute>
                  <MedicationSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute>
                  <Insights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-insights"
              element={
                <ProtectedRoute>
                  <HealthInsightsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/nutrition"
              element={
                <ProtectedRoute>
                  <Nutrition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/care-services"
              element={
                <ProtectedRoute>
                  <CareServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <ProtectedRoute>
                  <SubscriptionPlans />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription-success"
              element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription-cancel"
              element={
                <ProtectedRoute>
                  <SubscriptionCancel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trends"
              element={
                <ProtectedRoute>
                  <Trends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/talk-to-specialist"
              element={
                <ProtectedRoute>
                  <TalkToSpecialist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expert-support"
              element={
                <ProtectedRoute>
                  <ExpertSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-hub"
              element={
                <ProtectedRoute>
                  <HealthHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/privacy"
              element={
                <ProtectedRoute>
                  <PrivacyNotice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support-help"
              element={
                <ProtectedRoute>
                  <SupportHelp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/login"
              element={<AdminLogin />}
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute adminOnly>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/insights"
              element={
                <ProtectedRoute adminOnly>
                  <AdminInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-analytics"
              element={
                <ProtectedRoute adminOnly>
                  <EmailAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-trace"
              element={
                <ProtectedRoute adminOnly>
                  <AdminEmailTrace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:userId/sanity"
              element={
                <ProtectedRoute adminOnly>
                  <AdminUserCheckinSanity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/support-tickets"
              element={
                <ProtectedRoute adminOnly>
                  <AdminSupportTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health-alerts"
              element={
                <ProtectedRoute adminOnly>
                  <AdminHealthAlerts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedback-analytics"
              element={
                <ProtectedRoute adminOnly>
                  <AdminFeedbackAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/step-tracker"
              element={
                <ProtectedRoute>
                  <StepTrackerEnhanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <CommunityHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/preventive-health"
              element={
                <ProtectedRoute>
                  <PreventiveHealthTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-risk-check"
              element={
                <ProtectedRoute>
                  <HealthRiskCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hormonal-health"
              element={
                <ProtectedRoute>
                  <HormonalHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mental-health"
              element={
                <ProtectedRoute>
                  <MentalHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/education"
              element={
                <ProtectedRoute>
                  <EducationHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wellness"
              element={
                <ProtectedRoute>
                  <WellnessCheckIn />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/reminders"
              element={
                <ProtectedRoute>
                  <RemindersSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/womens-health"
              element={
                <ProtectedRoute>
                  <WomensHealth />
                </ProtectedRoute>
              }
            />

            <Route
              path="/mens-health"
              element={
                <ProtectedRoute>
                  <MensHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health"
              element={
                <ProtectedRoute>
                  <ChildrensHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/checkin"
              element={
                <ProtectedRoute>
                  <ChildDailyCheckin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/quick-check"
              element={
                <ProtectedRoute>
                  <ChildQuickCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/immunization"
              element={
                <ProtectedRoute>
                  <ChildImmunization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/growth"
              element={
                <ProtectedRoute>
                  <ChildGrowth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/nutrition"
              element={
                <ProtectedRoute>
                  <ChildNutrition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health/behaviour"
              element={
                <ProtectedRoute>
                  <ChildBehaviour />
                </ProtectedRoute>
              }
            />
            <Route
              path="/childrens-health-legacy"
              element={
                <ProtectedRoute>
                  <ChildrensHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health"
              element={
                <ProtectedRoute>
                  <MaternalHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/checkin"
              element={
                <ProtectedRoute>
                  <MaternalDailyCheckin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/symptoms"
              element={
                <ProtectedRoute>
                  <MaternalSymptoms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/week"
              element={
                <ProtectedRoute>
                  <MaternalWeek />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/nutrition"
              element={
                <ProtectedRoute>
                  <MaternalNutrition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/appointments"
              element={
                <ProtectedRoute>
                  <MaternalAppointments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/postpartum"
              element={
                <ProtectedRoute>
                  <MaternalPostpartum />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health/trends"
              element={
                <ProtectedRoute>
                  <MaternalTrends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maternal-health-legacy"
              element={
                <ProtectedRoute>
                  <MaternalHealth />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-reminders"
              element={
                <ProtectedRoute>
                  <MyReminders />
                </ProtectedRoute>
              }
            />

            <Route
              path="/family-health"
              element={
                <ProtectedRoute>
                  <UnifiedFamilyHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/family-health-education"
              element={
                <ProtectedRoute>
                  <FamilyHealth />
                </ProtectedRoute>
              }
            />


            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          )}
          <BottomNav />
          <Toaster />
        </div>
  );
}

export default App;
